<template>
	<div class="gantogram-container" v-if="calculated">
		<div
			@scroll="calculateSidebarLeft"
			ref="scrollContainer"
			class="gantogram-scroll-container"
			:style="{ height: totalHeight + bottomScrollMargin + 'px' }"
		>
			<div
				:class="{
					'gantogram-horizontal-line': !$vuetify.theme.dark,
					'gantogram-horizontal-line-dark-theme': $vuetify.theme.dark,
				}"
				:style="{
					top: locations.rows['row' + key].top + eventHeight * locations.rows['row' + key].size - 1 + 'px',
					left: sidebarWidth + 'px',
					width: dayWidth * dates.length + 'px',
				}"
				v-for="(row, key) in rows"
				:key="'horizontalLine' + key"
			></div>

			<div
				:class="{
					'gantogram-vertical-line': !$vuetify.theme.dark,
					'gantogram-vertical-line-dark-theme': $vuetify.theme.dark,
				}"
				:style="{
					top: headerHeight + 'px',
					left: getLeftForDate(date) + dayWidth - 1 + 'px',
					height: totalHeight - headerHeight + 'px',
				}"
				v-for="(date, key) in dates"
				:key="'verticalLine' + key"
			></div>

			<div
				@click="eventClicked(event)"
				:title="event.title"
				class="gantogram-event"
				v-for="(event, key) in events"
				:key="'event' + key"
				:style="{
					'line-height': eventHeight + 'px',
					'height': eventHeight - 6 + 'px',
					'top': locations.events['event' + key].top + 3 + 'px',
					'left': locations.events['event' + key].left + 3 + 'px',
					'width': locations.events['event' + key].width - 6 + 'px',
					'background': event.background,
					'color': event.color,
					'display': locations.events['event' + key].display,
				}"
				v-html="event.html"
			></div>

			<div
				:class="{
					'gantogram-header-item': !$vuetify.theme.dark && true,
					'gantogram-header-item-dark-theme': $vuetify.theme.dark && true,
					'gantogram-header-item-last': key === dates.length - 1,
				}"
				v-for="(date, key) in dates"
				:key="date"
				:style="{
					'line-height': headerHeight + 'px',
					'height': headerHeight + 'px',
					'width': dayWidth + 'px',
					'left': getLeftForDate(date) + 'px',
					'top': headerTop + 'px',
				}"
			>
				{{ reformatHeader(date) }}
			</div>

			<div
				:class="{
					'gantogram-sidebar-item': !$vuetify.theme.dark && true,
					'gantogram-sidebar-item-dark-theme': $vuetify.theme.dark && true,
					'gantogram-sidebar-item-last': key === rows.length - 1 && !$vuetify.theme.dark,
					'gantogram-sidebar-item-last-dark-theme': key === rows.length - 1 && $vuetify.theme.dark,
				}"
				:style="{
					'top': locations.rows['row' + key].top + 'px',
					'left': sidebarLeft + 'px',
					'line-height': eventHeight + 'px',
					'height': locations.rows['row' + key].size * eventHeight + 'px',
					'width': sidebarWidth + 'px',
				}"
				v-for="(row, key) in rows"
				:key="'row' + key"
			>
				{{ row }}
			</div>

			<div
				:class="{
					'gantogram-corner': !$vuetify.theme.dark,
					'gantogram-corner-dark-theme': $vuetify.theme.dark,
				}"
				:style="{
					top: headerTop + 'px',
					left: sidebarLeft + 'px',
					height: headerHeight + 'px',
					width: sidebarWidth + 'px',
				}"
			></div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'Gantogram',
	props: {
		dateFrom: String,
		dateTo: String,
		headerFormat: {
			type: String,
			default: 'D.M.',
		},
		dayWidth: {
			type: Number,
			default: 60,
		},
		headerHeight: {
			type: Number,
			default: 50,
		},
		eventHeight: {
			type: Number,
			default: 50,
		},
		sidebarWidth: {
			type: Number,
			default: 100,
		},
		events: {
			type: Array,
			default() {
				return [];
			},
		},
		categories: {
			type: Array,
			default() {
				return [];
			},
		},
	},
	emits: ['clicked'],
	data() {
		return {
			rows: [],
			locations: {
				events: {},
				rows: {},
			},
			calculated: false,
			sidebarLeft: 0,
			headerTop: 0,
			initialHeaderOffset: 0,
			bottomScrollMargin: 17,
		};
	},
	watch: {
		events: {
			handler: function () {
				this.getRows();
				this.calculateLocations();
			},
			deep: true,
		},
		dateFrom: {
			handler: function () {
				this.calculateLocations();
			},
			deep: true,
		},
		dateTo: {
			handler: function () {
				this.calculateLocations();
			},
			deep: true,
		},
	},
	computed: {
		dates() {
			let moment = require('moment');
			let currentDate = moment(this.dateFrom, 'YYYY-MM-DD');
			let endDate = moment(this.dateTo, 'YYYY-MM-DD');
			let dates = [];
			while (currentDate.format('YYYY-MM-DD') <= endDate.format('YYYY-MM-DD')) {
				dates.push(currentDate.format('YYYY-MM-DD'));
				currentDate = currentDate.add(1, 'd');
			}
			return dates;
		},
		totalHeight() {
			let height = this.headerHeight;
			for (let row in this.rows) {
				if (this.locations.rows['row' + row] === undefined) continue;
				let rowSize = this.locations.rows['row' + row].size;
				height += rowSize * this.eventHeight;
			}
			return height;
		},
	},
	methods: {
		calculateLocations() {
			this.calculated = false;
			let moment = require('moment');

			this.locations = {
				events: {},
				rows: {},
			};

			// get locations of rows
			for (let rowIdx in this.rows) {
				let row = this.rows[rowIdx];
				let location = {
					top: 0,
					size: 0,
				};

				// get row size
				location.size = 0;
				let currentDate = moment(this.dateFrom, 'YYYY-MM-DD');
				let endDate = moment(this.dateTo, 'YYYY-MM-DD');
				while (currentDate.format('YYYY-MM-DD') <= endDate.format('YYYY-MM-DD')) {
					let formattedDate = currentDate.format('YYYY-MM-DD');
					let maxEventsDay = 0;
					for (let event of this.events) {
						if (event.row !== row) continue;
						if (formattedDate >= event.startDate && formattedDate <= event.endDate) {
							maxEventsDay++;
						}
					}
					if (maxEventsDay > location.size) location.size = maxEventsDay;
					currentDate = currentDate.add(1, 'd');
				}
				if (location.size < this.getMinimumSizeForRow(row)) location.size = this.getMinimumSizeForRow(row);
				// get row top
				location.top = this.headerHeight;
				for (let rowIdxTmp = 0; rowIdxTmp < rowIdx; rowIdxTmp++) {
					location.top += this.locations.rows['row' + rowIdxTmp].size * this.eventHeight;
				}

				this.locations.rows['row' + rowIdx] = location;
			}

			// get locations of events
			for (let eventIdx in this.events) {
				let event = this.events[eventIdx];
				let eventStartDate = moment(event.startDate, 'YYYY-MM-DD');
				let eventEndDate = moment(event.endDate, 'YYYY-MM-DD');

				let location = {
					left: 0,
					top: 0,
					width: 0,
					subRow: 0,
					display: 'block',
				};

				// get event left
				location.left = this.getLeftForDate(event.startDate);

				// get event top
				location.subRow = 0;
				let overlap = true;
				while (overlap) {
					overlap = false;
					for (let eventIdxTmp = 0; eventIdxTmp < eventIdx; eventIdxTmp++) {
						let eventTmp = this.events[eventIdxTmp];
						if (eventTmp.row !== event.row) continue;
						if (this.locations.events['event' + eventIdxTmp].subRow !== location.subRow) continue;

						let tmpEventStartDate = moment(eventTmp.startDate, 'YYYY-MM-DD');
						let tmpEventEndDate = moment(eventTmp.endDate, 'YYYY-MM-DD');
						if (
							!(
								(eventStartDate < tmpEventStartDate && eventEndDate < tmpEventStartDate) ||
								(eventStartDate > tmpEventEndDate && eventEndDate > tmpEventEndDate)
							)
						) {
							overlap = true;
							location.subRow++;
							break;
						}
					}
				}
				location.top =
					this.locations.rows['row' + this.getIndexForRow(event.row)].top +
					location.subRow * this.eventHeight;

				//get event width
				if (eventStartDate.format('YYYY-MM-DD') < this.dateFrom)
					eventStartDate = moment(this.dateFrom, 'YYYY-MM-DD');
				if (eventEndDate.format('YYYY-MM-DD') > this.dateTo) eventEndDate = moment(this.dateTo, 'YYYY-MM-DD');
				location.width = (eventEndDate.diff(eventStartDate, 'days') + 1) * this.dayWidth;

				//get event display
				if (
					(event.startDate < this.dateFrom && event.endDate < this.dateFrom) ||
					(event.startDate > this.dateTo && event.endDate > this.dateTo)
				) {
					location.display = 'none';
				}

				this.locations.events['event' + eventIdx] = location;
			}

			this.calculated = true;
			this.$nextTick(() => {
				this.initialHeaderOffset = this.$refs.scrollContainer.offsetTop;
			});
		},
		getRows() {
			this.rows = [];
			for (let category of this.categories) {
				this.rows.push(category.label);
			}
		},
		reformatHeader(formatted) {
			let moment = require('moment');
			let date = moment(formatted, 'YYYY-MM-DD');
			return date.format(this.headerFormat);
		},
		getIndexForDate(date) {
			let moment = require('moment');
			let startDate = moment(this.dateFrom, 'YYYY-MM-DD');
			let endDate = moment(date, 'YYYY-MM-DD');
			return endDate.diff(startDate, 'days');
		},
		getIndexForRow(row) {
			for (let rowIdx in this.rows) {
				if (this.rows[rowIdx] === row) return rowIdx;
			}
			return null;
		},
		getMinimumSizeForRow(row) {
			for (let category of this.categories) {
				if (category.label === row && category.minimumSize > 0) return category.minimumSize;
			}
			return 1;
		},
		getLeftForDate(date) {
			if (date < this.dateFrom) date = this.dateFrom;
			return this.sidebarWidth + this.getIndexForDate(date) * this.dayWidth;
		},
		eventClicked(event) {
			this.$emit('clicked', event);
		},
		calculateSidebarLeft() {
			this.sidebarLeft = this.$refs.scrollContainer.scrollLeft;
		},
		calculateHeaderTop() {
			if (window.scrollY > this.initialHeaderOffset) {
				this.headerTop = window.scrollY - this.initialHeaderOffset - 16;
			} else {
				this.headerTop = 0;
			}
		},
	},
	mounted() {
		let vm = this;
		window.onscroll = function () {
			vm.calculateHeaderTop();
		};
	},
};
</script>

<style scoped>
.gantogram-scroll-container {
	position: relative;
	overflow-x: auto;
	overflow-y: hidden;
	height: 100%;
}

.gantogram-scroll-container-dark-theme {
	position: relative;
	overflow-x: auto;
	overflow-y: hidden;
	height: 100%;
}

.gantogram-header-item {
	position: absolute;
	text-align: center;
	border: 1px solid lightgrey;
	border-bottom: 2px solid lightgrey;
	border-left: none;
	color: dimgray;
	box-sizing: border-box;
	background: #f3f3f3;
}

.gantogram-header-item-dark-theme {
	position: absolute;
	text-align: center;
	border: 1px solid lightgrey;
	border-bottom: 2px solid lightgrey;
	border-left: none;
	color: rgba(255, 255, 255, 0.7);
	box-sizing: border-box;
	background: #3e3e3e;
}

.gantogram-header-item-last {
	border-right: 1px solid lightgrey;
}

.gantogram-sidebar-item {
	position: absolute;
	padding-left: 10px;
	padding-right: 10px;
	border: 1px solid lightgrey;
	border-right: 2px solid lightgrey;
	border-top: none;
	color: dimgray;
	box-sizing: border-box;
	background: #f3f3f3;
}

.gantogram-sidebar-item-dark-theme {
	position: absolute;
	padding-left: 10px;
	padding-right: 10px;
	border: 1px solid lightgrey;
	border-right: 2px solid lightgrey;
	border-top: none;
	color: rgba(255, 255, 255, 0.7);
	box-sizing: border-box;
	background: #3e3e3e;
}

.gantogram-sidebar-item-last {
	border-bottom: 1px solid lightgrey;
}

.gantogram-sidebar-item-last-dark-theme {
	border-bottom: 1px solid #b3b3b3;
}

.gantogram-event {
	position: absolute;
	padding-left: 10px;
	padding-right: 10px;
	cursor: pointer;
	overflow: hidden;
	border-radius: 10px;
}

.gantogram-horizontal-line {
	position: absolute;
	height: 1px;
	background: lightgrey;
}

.gantogram-horizontal-line-dark-theme {
	position: absolute;
	height: 1px;
	background: #808080;
}

.gantogram-vertical-line {
	position: absolute;
	width: 1px;
	background: lightgrey;
}

.gantogram-vertical-line-dark-theme {
	position: absolute;
	width: 1px;
	background: #808080;
}

.gantogram-corner {
	position: absolute;
	background: white;
	border-right: 2px solid lightgrey;
	border-bottom: 2px solid lightgrey;
}

.gantogram-corner-dark-theme {
	position: absolute;
	background: #3e3e3e;
	border-right: 2px solid lightgrey;
	border-bottom: 2px solid lightgrey;
}
</style>
